/* eslint-disable react-hooks/exhaustive-deps */
import Popper, { PopperPlacementType } from '@material-ui/core/Popper';
import styled from '@emotion/styled';
import * as React from 'react';
import { IGroupUser } from 'types/groups';
import {
  GROUP_LEARNER_PROFILE_CLICKED,
  PLANNER_PAGE
} from '@constants/segment';
import useGoalEventProps from '@hooks/useGoalEventProps';
import { segment } from '@actions/analytics';
import { GroupsContext } from '@context/GroupsContext';
import ProfilePopupContent from './ProfilePopupContent';

export interface IGroupProfilePopupProps {
  children: React.ReactNode;
  goalUID: string;
  direction: PopperPlacementType;
  closeDrawer: () => void;
}

export interface IGroupProfilePopupFn {
  handleShowPopup: (event: any, user: IGroupUser) => void;
  handleHidePopup: () => void;
}

const StyPopper = styled(Popper)`
  z-index: 1301;
`;

const ClickAwayWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const GroupProfilePopup = React.forwardRef<
  IGroupProfilePopupFn,
  IGroupProfilePopupProps
>(({ children, goalUID, direction, closeDrawer }, ref) => {
  const [open, setOpen] = React.useState(false);
  const [selectedUser, setSelectedUser] = React.useState<IGroupUser>(null);
  const [popupAnchorEl, setPopupAnchorEl] =
    React.useState<HTMLDivElement>(null);
  const groupsContext = React.useContext(GroupsContext);
  const { users, groupUid } = groupsContext;
  const goalSegmentData = useGoalEventProps({ goalUID });

  React.useImperativeHandle(ref, () => ({ handleShowPopup, handleHidePopup }));

  React.useEffect(() => {
    const drawer = document.querySelector(
      '.Groups-DrawerPaper .MuiDrawer-paper'
    );
    if (drawer) {
      drawer.addEventListener('scroll', () => {
        setOpen(false);
      });
    }
  }, []);

  React.useEffect(() => {
    if (selectedUser && selectedUser.username) {
      segment.track(GROUP_LEARNER_PROFILE_CLICKED, {
        ...goalSegmentData,
        last_primary_source: PLANNER_PAGE,
        last_primary_source_section: 'Planner',
        group_size: users.length,
        profile_uid: selectedUser.uid,
        profile_username: selectedUser.username
      });
    }
  }, [selectedUser]);

  React.useEffect(() => {
    if (selectedUser) {
      const user = users.find((userInfo) => userInfo.uid === selectedUser.uid);
      setSelectedUser(user);
    }
  }, [users]);

  const handleShowPopup = (event, targetUser) => {
    event.stopPropagation();
    const updatedOpen = selectedUser === targetUser ? !open : true;
    if (updatedOpen) {
      setSelectedUser(targetUser);
      setPopupAnchorEl(event.currentTarget);
    }
    setOpen(updatedOpen);
  };

  const handleHidePopup = () => {
    setOpen(false);
    setSelectedUser(null);
    setPopupAnchorEl(null);
  };

  const closeDrawerAndPopup = () => {
    handleHidePopup();
    if (closeDrawer) closeDrawer();
  };

  return (
    <ClickAwayWrapper>
      {children}
      <StyPopper
        id="GroupsLearnerPopup"
        open={open}
        anchorEl={popupAnchorEl}
        placement={direction}
        modifiers={{
          flip: {
            enabled: true
          },
          preventOverflow: {
            boundariesElement: document
          },
          hide: { enabled: true }
        }}
      >
        <ProfilePopupContent
          selectedUser={selectedUser}
          groupUID={groupUid}
          closeDrawerAndPopup={closeDrawerAndPopup}
        />
      </StyPopper>
    </ClickAwayWrapper>
  );
});

GroupProfilePopup.displayName = 'GroupProfilePopup';
export default GroupProfilePopup;
